import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Copy, ExternalLink, FileCheck2, SearchCheck } from 'lucide-react';
import LaborStandaloneLayout from '../../components/labor/LaborStandaloneLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { getLaborCaseByTracking } from '../../features/labor-intake/laborIntakeService';
import { useLaborPortalLanguage } from '../../features/labor-intake/LaborPortalLanguageContext';

const LaborIntakeSuccessPage = () => {
  const { isArabic, text } = useLaborPortalLanguage();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const referenceNumber = searchParams.get('ref');
  const trackingToken = searchParams.get('token');

  const [createdCase, setCreatedCase] = useState(location.state?.createdCase || null);

  useEffect(() => {
    if (!createdCase && referenceNumber && trackingToken) {
      getLaborCaseByTracking({ referenceNumber, trackingToken }).then(setCreatedCase).catch(() => setCreatedCase(null));
    }
  }, [createdCase, referenceNumber, trackingToken]);

  const trackingPath = createdCase
    ? `/track/${encodeURIComponent(createdCase.trackingToken)}`
    : '/labor-intake';

  const fullTrackingUrl = `${window.location.origin}${trackingPath}`;

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  return (
    <LaborStandaloneLayout footerNote={text('تم إنشاء صفحة مستقلة للمتابعة إلى حين إطلاق منصة وازن العامة.', 'A dedicated tracking page has been created until the public Wazin platform is launched.')}>
      <main className="px-4 pb-20 pt-12">
          <div className="mx-auto max-w-4xl">
            <Card className="overflow-hidden rounded-[32px] border-[#C8A870]/20 shadow-[0_25px_80px_rgba(31,59,100,0.12)]">
              <div className="bg-gradient-to-l from-[#16315a] via-[#1F3B64] to-[#264b7a] px-8 py-10 text-white">
                <div className="mb-5 inline-flex h-20 w-20 items-center justify-center rounded-[28px] bg-white/12">
                  <CheckCircle2 className="h-10 w-10 text-[#F0DDBE]" />
                </div>
                <h1 className="text-3xl font-black sm:text-4xl">{text('تم استلام طلبكم بنجاح', 'Your request has been received successfully')}</h1>
                <p className="mt-4 max-w-2xl text-base leading-8 text-slate-200">
                  {text('أُنشئ الطلب داخل بوابة الاستقبال العمالية، ويمكنكم الآن الاحتفاظ بالرقم المرجعي ورابط المتابعة الخاص للاطلاع على الحالة ورفع مستندات إضافية لاحقاً.', 'The request was created through the labor intake portal. You can now keep the reference number and private tracking link to review the status and upload additional documents later.')}
                </p>
              </div>

              <CardContent className="grid gap-6 p-6 sm:p-8">
                <div className="grid gap-4 md:grid-cols-2">
                  <InfoBox
                    icon={FileCheck2}
                    title={text('الرقم المرجعي', 'Reference Number')}
                    value={createdCase?.referenceNumber || (text('غير متوفر', 'Not available'))}
                    actionLabel={text('نسخ الرقم', 'Copy Number')}
                    onAction={() => createdCase && handleCopy(createdCase.referenceNumber)}
                  />
                  <InfoBox
                    icon={SearchCheck}
                    title={text('رابط المتابعة الخاص', 'Private Tracking Link')}
                    value={fullTrackingUrl}
                    actionLabel={text('نسخ الرابط', 'Copy Link')}
                    onAction={() => handleCopy(fullTrackingUrl)}
                    compact
                  />
                </div>

                <div className="rounded-[24px] border border-[#C8A870]/18 bg-[#f8f6f1] p-5 text-sm leading-8 text-slate-700">
                  {text('يرجى الاحتفاظ بهذا الرابط وعدم مشاركته إلا مع صاحب الطلب، إذ إنه يفتح صفحة المتابعة الخاصة بالعميل ويعرض حالة الطلب فقط دون أي ملاحظات داخلية.', 'Please keep this link private and do not share it except with the request owner. It opens the client tracking page and displays only the request status without any internal notes.')}
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button asChild className="rounded-xl bg-[#1F3B64] px-6 py-6 text-white hover:bg-[#264b7a]">
                    <Link to={trackingPath}>
                      {text('فتح صفحة المتابعة', 'Open Tracking Page')}
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-xl border-[#C8A870]/35 px-6 py-6 text-[#1F3B64]">
                    <Link to="/labor-intake/new">
                      {text('إنشاء طلب جديد', 'Create New Request')}
                      <Copy className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
      </main>
    </LaborStandaloneLayout>
  );
};

const InfoBox = ({ icon: Icon, title, value, actionLabel, onAction, compact = false }) => (
  <div className="rounded-[24px] border border-[#C8A870]/18 bg-white p-5 shadow-sm">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1F3B64] text-white">
      <Icon className="h-5 w-5" />
    </div>
    <p className="text-sm font-bold text-[#1F3B64]">{title}</p>
    <p className={`mt-2 font-semibold text-slate-700 ${compact ? 'break-all text-sm leading-7' : 'text-xl'}`}>{value}</p>
    <button type="button" onClick={onAction} className="mt-4 text-sm font-bold text-[#A27849]">
      {actionLabel}
    </button>
  </div>
);

export default LaborIntakeSuccessPage;
